import type { ConfigurationOperation } from "@repo/app-types/entities/configurationAgent";
import {
  getOperationCategory,
  getOperationLabel,
} from "@repo/app-types/helpers/configurationAgentParser";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import {
  IconCheck,
  IconCircleMinus,
  IconCirclePlus,
  IconPencil,
} from "@tabler/icons-react";
import { useState, type FC } from "react";
import { twMerge } from "tailwind-merge";
import { WordDiffView } from "../components/wordDiffView.js";
import { useConfigurationAssistantContext } from "../configurationAssistant/configurationAssistantContext.js";
import { useSignalEditorState } from "./signalEditorStateContext.js";

export interface SignalOperationCardProps {
  operationId: string;
  operation: ConfigurationOperation;
}

export const SignalOperationCard: FC<SignalOperationCardProps> = ({
  operationId,
  operation,
}) => {
  const { applyOperation, isOperationApplied } =
    useConfigurationAssistantContext();
  const [isLoading, setIsLoading] = useState(false);

  const category = getOperationCategory(operation);
  const label = getOperationLabel(operation);
  const isApplied = isOperationApplied(operationId);

  const handleApply = async () => {
    setIsLoading(true);
    try {
      await applyOperation(operationId, operation);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className={twMerge(
        "overflow-hidden rounded-xl border border-border-light bg-white shadow-xs",
        category === "add" && "border-emerald-200",
        category === "update" && "border-blue-200",
        category === "remove" && "border-rose-200",
      )}
    >
      {/* Header */}
      <div
        className={twMerge(
          "flex items-center gap-2 px-3 py-2",
          category === "add" && "bg-emerald-50",
          category === "update" && "bg-blue-50",
          category === "remove" && "bg-rose-50",
        )}
      >
        <OperationIcon category={category} />
        <span
          className={twMerge(
            "text-xs font-semibold",
            category === "add" && "text-emerald-700",
            category === "update" && "text-blue-700",
            category === "remove" && "text-rose-700",
          )}
        >
          {label}
        </span>
      </div>

      {/* Content */}
      <div className="px-3 py-2.5">
        <SignalOperationDetails operation={operation} />
      </div>

      {/* Apply button */}
      <div className="border-t border-gray-100 px-3 py-2">
        <button
          type="button"
          onClick={handleApply}
          disabled={isApplied || isLoading}
          className={twMerge(
            "flex w-full items-center justify-center gap-1.5 rounded-lg py-1.5 text-xs font-medium transition-colors",
            isApplied || isLoading
              ? "cursor-default bg-gray-100 text-muted-foreground"
              : twMerge(
                  category === "add" &&
                    "bg-emerald-100 text-emerald-700 hover:bg-emerald-200",
                  category === "update" &&
                    "bg-blue-100 text-blue-700 hover:bg-blue-200",
                  category === "remove" &&
                    "bg-rose-100 text-rose-700 hover:bg-rose-200",
                ),
          )}
        >
          {isLoading ? (
            <>
              <Loader style={LoaderStyle.SPINNER} />
              Applying...
            </>
          ) : isApplied ? (
            <>
              <IconCheck className="size-3.5" />
              Change applied
            </>
          ) : (
            "Apply change"
          )}
        </button>
      </div>
    </div>
  );
};

const OperationIcon: FC<{ category: "add" | "update" | "remove" }> = ({
  category,
}) => {
  const iconClass = twMerge(
    "size-4",
    category === "add" && "text-emerald-600",
    category === "update" && "text-blue-600",
    category === "remove" && "text-rose-600",
  );

  switch (category) {
    case "add":
      return <IconCirclePlus className={iconClass} />;
    case "update":
      return <IconPencil className={iconClass} />;
    case "remove":
      return <IconCircleMinus className={iconClass} />;
  }
};

const SignalOperationDetails: FC<{ operation: ConfigurationOperation }> = ({
  operation,
}) => {
  switch (operation.type) {
    case "update-signal":
      return (
        <div className="space-y-1.5">
          {operation.name && <FieldRow label="Name" value={operation.name} />}
          {operation.description && (
            <DescriptionFieldRow proposedDescription={operation.description} />
          )}
        </div>
      );

    case "add-metadata-field":
      return (
        <div className="space-y-1.5">
          <FieldRow label="Field" value={operation.key} />
          <FieldRow label="Type" value={operation.fieldType} />
          {operation.options && operation.options.length > 0 && (
            <FieldRow label="Options" value={operation.options.join(", ")} />
          )}
        </div>
      );

    case "update-metadata-field":
      return (
        <div className="space-y-1.5">
          <FieldRow label="Field" value={operation.key} />
          {operation.addOptions && operation.addOptions.length > 0 && (
            <FieldRow
              label="Add options"
              value={operation.addOptions.join(", ")}
            />
          )}
          {operation.removeOptions && operation.removeOptions.length > 0 && (
            <FieldRow
              label="Remove options"
              value={operation.removeOptions.join(", ")}
            />
          )}
        </div>
      );

    case "remove-metadata-field":
      return (
        <div className="space-y-1.5">
          <FieldRow label="Field" value={operation.key} />
        </div>
      );

    default:
      return null;
  }
};

const DescriptionFieldRow: FC<{ proposedDescription: string }> = ({
  proposedDescription,
}) => {
  const { currentDescription } = useSignalEditorState();
  return (
    <DiffFieldRow
      label="Description"
      current={currentDescription}
      proposed={proposedDescription}
    />
  );
};

const DiffFieldRow: FC<{
  label: string;
  current: string;
  proposed: string;
}> = ({ label, current, proposed }) => (
  <div>
    <div className="text-[10px] font-medium text-gray-400">{label}</div>
    <div className="mt-0.5 text-xs text-foreground">
      {current === proposed ? (
        proposed
      ) : (
        <WordDiffView savedBody={current} editedBody={proposed} />
      )}
    </div>
  </div>
);

const FieldRow: FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <div className="text-[10px] font-medium text-gray-400">{label}</div>
    <div className="mt-0.5 text-xs text-foreground">{value}</div>
  </div>
);
